/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { Credentials } from '@opengeoweb/api';
import { getHeaders, handleResponse } from '@opengeoweb/api';
import { sessionStorageProvider } from '@opengeoweb/shared';
import type {
  TemplatePresetsResponse,
  WorkspaceLabelsConfigResponse,
  WorkspacePresetForBE,
  WorkspacePresetFromBE,
  WorkspacePresetListItem,
} from '../../store/workspace/types';
import type { FetchWorkspaceParams } from '../../store/workspaceList/types';
import { LABELS_CONFIG_DEFAULT } from '../../utils/labels.config';
import {
  getPresetBaseUrl,
  getIdFromUrl,
  extractWorkspacePresetFromJSON,
  filterWorkspacePresetList,
  hasNoPresetBackend,
} from '../utils';

export const TEMPLATE_PRESETS_FILENAME = 'templatePresets.json';

export const getTemplatePresetsFilename = (): string =>
  sessionStorageProvider.getConfig()?.GW_TEMPLATE_PRESETS_FILENAME ||
  TEMPLATE_PRESETS_FILENAME;

export const getTemplatePresets = async (
  filename: string = getTemplatePresetsFilename(),
): Promise<TemplatePresetsResponse> => {
  const response = (await fetch(
    `./assets/${encodeURIComponent(filename)}`,
  ).then(handleResponse)) as TemplatePresetsResponse | undefined;

  return {
    workspacePresets: response?.workspacePresets ?? [],
    viewPresets: response?.viewPresets ?? [],
  };
};

export const getWorkspaceLabelsConfig = async (
  auth: Credentials,
): Promise<WorkspaceLabelsConfigResponse> => {
  if (hasNoPresetBackend()) {
    return LABELS_CONFIG_DEFAULT;
  }
  return fetch(`${getPresetBaseUrl()}/workspace-labels-config`, {
    headers: getHeaders(auth),
  }).then(handleResponse) as Promise<WorkspaceLabelsConfigResponse>;
};

export const getWorkspacePreset = async (
  workspaceId: string,
  auth?: Credentials,
): Promise<WorkspacePresetFromBE> => {
  if (hasNoPresetBackend()) {
    return extractWorkspacePresetFromJSON(workspaceId);
  }
  return fetch(`${getPresetBaseUrl()}/workspacepreset/${workspaceId}`, {
    headers: getHeaders(auth),
  }).then(handleResponse) as Promise<WorkspacePresetFromBE>;
};

export const getWorkspacePresets = async (
  auth?: Credentials,
  params?: FetchWorkspaceParams,
): Promise<WorkspacePresetListItem[]> => {
  if (hasNoPresetBackend()) {
    return filterWorkspacePresetList(params);
  }
  const presetsUrl = `${getPresetBaseUrl()}/workspacepreset`;
  const searchParams = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });
  }

  const queryString = searchParams.toString();
  const url = queryString ? `${presetsUrl}?${queryString}` : presetsUrl;

  return fetch(url, {
    headers: getHeaders(auth),
  }).then(handleResponse) as Promise<WorkspacePresetListItem[]>;
};

export const getWorkspace = async (
  workspaceId: string,
  auth: Credentials,
): Promise<WorkspacePresetFromBE> => {
  if (hasNoPresetBackend()) {
    return extractWorkspacePresetFromJSON(workspaceId);
  }
  return fetch(`${getPresetBaseUrl()}/workspacepreset/${workspaceId}`, {
    headers: getHeaders(auth),
  }).then(handleResponse) as Promise<WorkspacePresetFromBE>;
};

export const deleteWorkspace = async (
  workspaceId: string,
  auth: Credentials,
): Promise<null> =>
  fetch(`${getPresetBaseUrl()}/workspacepreset/${workspaceId}`, {
    headers: getHeaders(auth),
    method: 'DELETE',
  }).then(handleResponse) as Promise<null>;

export const updateWorkspace = async (
  workspaceId: string,
  data: WorkspacePresetForBE,
  auth: Credentials,
): Promise<null> =>
  fetch(`${getPresetBaseUrl()}/workspacepreset/${workspaceId}`, {
    headers: getHeaders(auth),
    method: 'PUT',
    body: JSON.stringify(data),
  }).then(handleResponse) as Promise<null>;

export const saveWorkspacePreset = async (
  workspaceId: string,
  data: WorkspacePresetForBE,
  auth?: Credentials,
): Promise<void> => {
  return fetch(`${getPresetBaseUrl()}/workspacepreset/${workspaceId}/`, {
    headers: getHeaders(auth),
    method: 'PUT',
    body: JSON.stringify(data),
  }).then(handleResponse) as Promise<void>;
};

export const createWorkspace = async (
  data: WorkspacePresetForBE,
  auth: Credentials,
): Promise<Headers> =>
  fetch(`${getPresetBaseUrl()}/workspacepreset`, {
    headers: getHeaders(auth),
    method: 'POST',
    body: JSON.stringify(data),
  }).then(handleResponse) as Promise<Headers>;

export const saveWorkspacePresetAs = async (
  data: WorkspacePresetForBE,
  auth?: Credentials,
): Promise<string> => {
  const response = await fetch(`${getPresetBaseUrl()}/workspacepreset`, {
    headers: getHeaders(auth),
    method: 'POST',
    body: JSON.stringify(data),
  });
  await handleResponse(response);
  const headerLocation =
    response.headers.get('Location') ?? response.headers.get('location');
  return getIdFromUrl(headerLocation!);
};
